/**
 * "View as role" — a LOOK-ONLY preview of the console as another role
 * (Akash, 2026-10-22).
 *
 * WHO. Only a person whose REAL role is platform_admin, support_l2 or
 * support_l3 is offered it, and they may pick any of the five roles in
 * `PREVIEWABLE_ROLES` except their own highest real role. A support_l2
 * looking as support_l3 is fine: nothing is granted, only shown.
 *
 * WHAT CHANGES. While a preview is active `RolePreviewProvider`
 * re-provides `AuthContext` with `roles` (and `user.roles`) set to the
 * previewed role, so every role decision in the tree -- `RoleGuard`,
 * `RoleGuardedElement`, `useHighestRole`, `audienceFor`,
 * `canSeeSimulated`, the help copy -- answers for that role without
 * any of them knowing a preview exists. The one decision the browser
 * does not make itself is the server's capability matrix, which is
 * computed for the caller's REAL token; `applyRolePreviewToCapabilities`
 * recomputes it for the previewed role from `ROLE_CAPABILITIES`, a
 * mirror of the backend's `ROLE_TO_CAPABILITIES` pinned by
 * `tests/unit/role-preview-capabilities-drift.test.ts`.
 *
 * WHAT DOES NOT. Reads keep going out with the real token, so the data
 * on screen is what the real role may read (a site list, say, can be
 * wider than the previewed role's). That is accepted: this is a look at
 * the console's SHAPE for a role, not an impersonation.
 *
 * THE WRITE BLOCK IS THE GUARANTEE. `assertWriteAllowed` refuses every
 * request whose method is not GET/HEAD/OPTIONS while a preview is
 * active, BEFORE anything is sent, by throwing `PreviewModeError`. It
 * is called from `apiFetch` (every v1 call) and from the global fetch
 * guard `installPreviewFetchGuard` installs at boot, which covers the
 * handful of call sites that use `fetch` directly. Disabled buttons
 * (every capability reads disabled with reason `preview_mode`) are a
 * courtesy on top, not the thing relied upon.
 *
 * WHERE IT LIVES. Module state here, mirrored to `sessionStorage` under
 * one key, so the gate in `apiFetch` can read it without React and a
 * reload of the same tab keeps the preview (and keeps the block on from
 * the very first request, before auth has even resolved). NEVER
 * `localStorage`: a preview must not outlive the tab, leak into a new
 * one, or greet the next person to sign in on a shared machine. The
 * stored record carries the user id it was started by, and the provider
 * drops it if anyone else is signed in.
 */
import type { CapabilityDisabledReason, CapabilityId, SiteCapabilities } from '../api/v1';
import { ROLE_PRECEDENCE, type RoleToken } from './types';
import { bareRole, normaliseRoles, type BareRole, type RoleLike } from './roles';

/** Real roles that are offered "View as…". */
export const PREVIEW_VIEWER_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:support_l2',
  'nexsoss:support_l3',
];

/** Roles that may be previewed, in the order the menu lists them. */
export const PREVIEWABLE_ROLES: readonly RoleToken[] = [
  'nexsoss:foh_operator',
  'nexsoss:site_supervisor',
  'nexsoss:service_operator',
  'nexsoss:support_l2',
  'nexsoss:support_l3',
];

const ROLE_LABELS: Record<RoleToken, string> = {
  'nexsoss:platform_admin': 'Platform admin',
  'nexsoss:support_l3': 'Support L3',
  'nexsoss:support_l2': 'Support L2',
  'nexsoss:site_supervisor': 'Site supervisor',
  'nexsoss:service_operator': 'Service operator',
  'nexsoss:foh_operator': 'FOH operator',
};

/** "FOH operator", "Site supervisor", … */
export function previewRoleLabel(role: RoleToken): string {
  return ROLE_LABELS[role];
}

function highestOf(roles: readonly RoleToken[]): RoleToken | null {
  for (const r of ROLE_PRECEDENCE) if (roles.includes(r)) return r;
  return null;
}

/** Whether these REAL roles are offered "View as…". */
export function canPreviewRoles(realRoles: Iterable<RoleLike> | null | undefined): boolean {
  return normaliseRoles(realRoles).some((r) => PREVIEW_VIEWER_ROLES.includes(r));
}

/** The roles these REAL roles may pick: none unless `canPreviewRoles`,
 *  and never the viewer's own highest real role. */
export function previewChoicesFor(realRoles: Iterable<RoleLike> | null | undefined): RoleToken[] {
  const held = normaliseRoles(realRoles);
  if (!held.some((r) => PREVIEW_VIEWER_ROLES.includes(r))) return [];
  const own = highestOf(held);
  return PREVIEWABLE_ROLES.filter((r) => r !== own);
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface PreviewState {
  readonly role: RoleToken;
  /** The real user who started it; a different sign-in drops it. */
  readonly userId: string;
}

export const ROLE_PREVIEW_STORAGE_KEY = 'nexsoss:role-preview-v1';

let state: PreviewState | null | undefined; // undefined = not yet hydrated
const listeners = new Set<() => void>();

function readStored(): PreviewState | null {
  try {
    const raw = window.sessionStorage.getItem(ROLE_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { role?: unknown; userId?: unknown };
    if (typeof parsed.userId !== 'string' || !parsed.userId) return null;
    const [role] = normaliseRoles([typeof parsed.role === 'string' ? parsed.role : null]);
    if (!role || !PREVIEWABLE_ROLES.includes(role)) return null;
    return { role, userId: parsed.userId };
  } catch {
    return null;
  }
}

function writeStored(next: PreviewState | null): void {
  try {
    if (next) window.sessionStorage.setItem(ROLE_PREVIEW_STORAGE_KEY, JSON.stringify(next));
    else window.sessionStorage.removeItem(ROLE_PREVIEW_STORAGE_KEY);
  } catch {
    // Storage blocked: the preview still works for this page, it just
    // does not survive a reload -- the safe direction to fail in.
  }
}

/** The active preview, or null. Stable reference between changes (it
 *  is a `useSyncExternalStore` snapshot). */
export function getPreviewState(): PreviewState | null {
  if (state === undefined) state = typeof window === 'undefined' ? null : readStored();
  return state;
}

export function getPreviewRole(): RoleToken | null {
  return getPreviewState()?.role ?? null;
}

export function subscribePreview(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(): void {
  for (const l of [...listeners]) l();
}

export function startPreview(role: RoleToken, userId: string): void {
  if (!PREVIEWABLE_ROLES.includes(role)) throw new Error(`Cannot preview ${role}`);
  const current = getPreviewState();
  if (current && current.role === role && current.userId === userId) return;
  state = { role, userId };
  writeStored(state);
  emit();
}

export function clearPreview(): void {
  if (getPreviewState() === null) return;
  state = null;
  writeStored(null);
  emit();
}

/** Tests only: forget the in-memory state so the next read rehydrates
 *  from sessionStorage, as a reload would. */
export function __resetPreviewForTests(): void {
  state = undefined;
  listeners.clear();
  refusalListeners.clear();
}

// ---------------------------------------------------------------------------
// The write block
// ---------------------------------------------------------------------------

/** The `code` a refused write carries, for `apiErrorCode`-style readers. */
export const PREVIEW_MODE_CODE = 'preview_mode';

export function previewActionsOffText(role: RoleToken): string {
  return `Actions are off while viewing as ${previewRoleLabel(role)}`;
}

/**
 * A write refused because a role preview is active. Nothing was sent.
 *
 * Its `message` is the calm sentence itself, so a surface that renders
 * `error.message` already says the right thing. Extends `Error`, NOT
 * `TypeError`: `retry.ts::shouldRetry` retries TypeErrors, and retrying
 * a refusal that will refuse again is noise.
 */
export class PreviewModeError extends Error {
  readonly code = PREVIEW_MODE_CODE;
  readonly method: string;
  readonly url: string;
  readonly role: RoleToken;

  constructor(method: string, url: string, role: RoleToken) {
    super(`${previewActionsOffText(role)} — nothing was sent.`);
    this.name = 'PreviewModeError';
    this.method = method;
    this.url = url;
    this.role = role;
  }
}

export function isPreviewModeError(err: unknown): err is PreviewModeError {
  if (err instanceof PreviewModeError) return true;
  // A wrapper that kept the cause (mutations sometimes rethrow).
  const cause = (err as { cause?: unknown } | null)?.cause;
  return cause instanceof PreviewModeError;
}

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

type RefusalListener = (err: PreviewModeError) => void;
const refusalListeners = new Set<RefusalListener>();

/** Called with every refusal, so the banner can say "not sent". */
export function onPreviewRefusal(listener: RefusalListener): () => void {
  refusalListeners.add(listener);
  return () => {
    refusalListeners.delete(listener);
  };
}

/**
 * Throws `PreviewModeError` when a preview is active and `method` is a
 * write. A no-op otherwise. Call BEFORE sending anything.
 */
export function assertWriteAllowed(method: string | undefined, url: string): void {
  const role = getPreviewRole();
  if (!role) return;
  const m = (method ?? 'GET').toUpperCase();
  if (READ_METHODS.has(m)) return;
  if (isPreviewExemptWrite(m, url)) return;
  const err = new PreviewModeError(m, url, role);
  for (const l of [...refusalListeners]) {
    try {
      l(err);
    } catch {
      // A listener's failure must never turn a refusal into a send.
    }
  }
  throw err;
}

/**
 * The two writes a preview lets through, and nothing else:
 *
 *  - the client telemetry sink (`/client-errors`, any method): it
 *    records what the browser did and changes nothing anyone operates;
 *  - `POST /api/v1/sites/{site}/audit/role-preview`, EXACTLY: the
 *    server audit row for entering and leaving the preview itself
 *    (api-server `api/audit.py::record_role_preview`, which refuses
 *    anyone the console would not offer the preview to).
 *
 * Matched on the URL's PATH, anchored at both ends, so a query string
 * or a longer path that merely contains the segment does not pass.
 */
const ROLE_PREVIEW_AUDIT_PATH = /\/api\/v1\/sites\/[^/]+\/audit\/role-preview$/;

function pathOf(url: string): string {
  try {
    return new URL(url, 'http://local.invalid').pathname;
  } catch {
    return url.split(/[?#]/)[0] ?? url;
  }
}

export function isPreviewExemptWrite(method: string, url: string): boolean {
  const path = pathOf(url);
  if (path.endsWith('/client-errors')) return true;
  return method.toUpperCase() === 'POST' && ROLE_PREVIEW_AUDIT_PATH.test(path);
}

function requestParts(input: RequestInfo | URL, init?: RequestInit): { method: string; url: string } {
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return { method: init?.method ?? input.method, url: input.url };
  }
  return { method: init?.method ?? 'GET', url: String(input) };
}

let guardInstalled = false;

/**
 * Wraps the global `fetch` so every write anywhere in the SPA is
 * refused during a preview -- including the call sites that bypass
 * `apiFetch` (`api/hooks/index.ts`, `useIssues`, `ProfileScreen`, …).
 * Installed once at boot from `main.tsx`; idempotent.
 */
export function installPreviewFetchGuard(target: typeof globalThis = globalThis): void {
  if (guardInstalled || typeof target.fetch !== 'function') return;
  const original = target.fetch.bind(target);
  target.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const { method, url } = requestParts(input, init);
    try {
      assertWriteAllowed(method, url);
    } catch (err) {
      return Promise.reject(err);
    }
    return original(input, init);
  };
  guardInstalled = true;
}

/** Tests only. */
export function __uninstallGuardFlagForTests(): void {
  guardInstalled = false;
}

// ---------------------------------------------------------------------------
// The capability matrix, recomputed for the previewed role
// ---------------------------------------------------------------------------

/**
 * Mirror of `ROLE_TO_CAPABILITIES` in `backend/api_server/policy/
 * capabilities.py`, which is authoritative. Used ONLY to answer "would
 * this role hold it" during a preview; nothing here grants anything.
 * `role-preview-capabilities-drift.test.ts` fails when the two differ.
 */
export const ROLE_CAPABILITIES: Readonly<Record<BareRole, ReadonlySet<string>>> = {
  platform_admin: new Set([
    'door.arm_writes', 'door.close', 'door.declare_open', 'door.open', 'door.revoke_override',
    'emergency.reset', 'emergency.trigger',
    'lift.abort', 'lift.acquire_session', 'lift.advance', 'lift.arm_writes', 'lift.end_session',
    'lift.manual_call', 'lift.manual_close', 'lift.recover', 'lift.release_session',
    'lift.request_destination', 'lift.rule_calendar', 'lift.rule_switch', 'lift.set_mode',
    'maintenance.manage',
    'platform.restart_component', 'platform.run_runbook', 'platform.view_health', 'platform.view_logs',
    'recovery.confirm', 'recovery.dismiss',
    'robot.commission', 'robot.confirm_dustbag', 'robot.decommission', 'robot.direct_action',
    'robot.fire_relocalize', 'robot.release_mutex', 'robot.set_idle_behavior',
    'schedule.manage',
    'task.cancel', 'task.delete_history', 'task.dispatch', 'task.interrupt', 'task.kill',
    'task.resume', 'task.retire', 'task.rewind', 'task.skip_phase', 'task.undo_phase',
    'ticket.triage',
  ]),
  site_supervisor: new Set([
    'door.arm_writes', 'door.close', 'door.declare_open', 'door.open', 'door.revoke_override',
    'emergency.reset', 'emergency.trigger',
    'lift.abort', 'lift.acquire_session', 'lift.advance', 'lift.arm_writes', 'lift.end_session',
    'lift.manual_call', 'lift.manual_close', 'lift.recover', 'lift.release_session',
    'lift.request_destination', 'lift.rule_calendar', 'lift.rule_switch', 'lift.set_mode',
    'maintenance.manage',
    'robot.commission', 'robot.confirm_dustbag', 'robot.decommission', 'robot.direct_action',
    'robot.fire_relocalize', 'robot.release_mutex', 'robot.set_idle_behavior',
    'schedule.manage',
    'task.cancel', 'task.delete_history', 'task.dispatch', 'task.interrupt', 'task.kill',
    'task.resume', 'task.rewind', 'task.skip_phase', 'task.undo_phase',
  ]),
  foh_operator: new Set([
    'robot.confirm_dustbag', 'robot.fire_relocalize',
    'schedule.manage',
    'task.cancel', 'task.dispatch', 'task.interrupt', 'task.kill', 'task.resume',
    'task.rewind', 'task.skip_phase', 'task.undo_phase',
  ]),
  service_operator: new Set([
    'door.close', 'door.open',
    'lift.abort', 'lift.acquire_session', 'lift.advance', 'lift.end_session', 'lift.manual_call',
    'lift.manual_close', 'lift.recover', 'lift.release_session', 'lift.request_destination',
    'lift.set_mode',
    'robot.confirm_dustbag', 'robot.fire_relocalize', 'robot.release_mutex',
  ]),
  support_l2: new Set([
    'platform.run_runbook', 'platform.view_health', 'platform.view_logs',
    'recovery.confirm', 'recovery.dismiss', 'ticket.triage',
  ]),
  support_l3: new Set([
    'door.declare_open', 'door.revoke_override',
    'platform.restart_component', 'platform.run_runbook', 'platform.view_health', 'platform.view_logs',
    'recovery.confirm', 'recovery.dismiss', 'ticket.triage',
  ]),
};

export function roleHoldsCapability(role: RoleToken, capability: CapabilityId | string): boolean {
  return ROLE_CAPABILITIES[bareRole(role)].has(capability);
}

/**
 * The server's capability answer, as the previewed role would receive
 * it, then switched off.
 *
 * The server computed `disabled_reasons` for the REAL token. Every
 * reason except `role_policy_denies` is about the site, not the caller
 * (worker state, certification, maintenance, …) and is kept; the role
 * reason is recomputed for `role` in the same order the backend adds it
 * (`CapabilityPolicy.evaluate`: only when no earlier reason -- i.e.
 * `site_assignment_missing` -- is present). Finally `preview_mode` is
 * appended to every capability the role WOULD hold, so the control is
 * drawn as the role would see it and is disabled: look only.
 *
 * Readers that branch on "held at all" (`useEmergencyCapabilities`,
 * which reads `role_policy_denies`) therefore answer for the previewed
 * role, and readers that branch on `enabled` draw a disabled button.
 */
export function applyRolePreviewToCapabilities(
  raw: SiteCapabilities,
  role: RoleToken | null,
): SiteCapabilities {
  if (!role) return raw;
  return {
    ...raw,
    capabilities: raw.capabilities.map((c) => {
      const reasons: string[] = c.disabled_reasons.filter((r) => r !== 'role_policy_denies');
      const held = roleHoldsCapability(role, c.capability);
      if (!held && !reasons.includes('site_assignment_missing')) reasons.unshift('role_policy_denies');
      if (held) reasons.push(PREVIEW_MODE_CODE);
      return {
        ...c,
        enabled: false,
        // `preview_mode` is a client-only reason: the backend enum has
        // no such member and never will. Every reader renders reasons
        // as codes or tests membership, so the wider string is safe.
        disabled_reasons: reasons as CapabilityDisabledReason[],
      };
    }),
  };
}

// ---------------------------------------------------------------------------
// Raw capability answers, kept so a preview change can recompute the
// cached matrix in place without a refetch (see RolePreviewProvider).
// ---------------------------------------------------------------------------

const rawCapabilities = new Map<string, SiteCapabilities>();

export function rememberRawCapabilities(site: string, raw: SiteCapabilities): void {
  rawCapabilities.set(site, raw);
}

export function rawCapabilitiesFor(site: string): SiteCapabilities | undefined {
  return rawCapabilities.get(site);
}
